import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../components/Toast';
import { ApiService } from '../utils/api';
import { videoThumbnail } from '../components/home/GalleryGrid';
import { formatDateTime } from '../utils/datetime';
import { Check, Copy, Eye, EyeOff, FolderOpen, FolderPlus, ImagePlus, Play, Star, Trash2, Video, X } from 'lucide-react';

const STATUS_STYLE = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-gray-200 text-gray-700',
};

const TestimonialsSection = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [filter, setFilter] = useState('PENDING');
  const { data: items = [] } = useQuery({ queryKey: ['adminTestimonials'], queryFn: () => ApiService.getAdminTestimonials() });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['adminTestimonials'] });
    queryClient.invalidateQueries({ queryKey: ['websiteContent'] });
  };
  const act = (promise, message) => promise.then(() => { refresh(); showSuccess(message); }).catch((e) => showError(e.message));
  const submitLink = `${window.location.origin}/testimonial`;
  const counts = items.reduce((acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }), {});
  const shown = items.filter((t) => filter === 'ALL' || t.status === filter);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Testimonials</CardTitle>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-600">Public link:</span>
          <code className="rounded bg-gray-100 px-2 py-1 text-xs break-all">{submitLink}</code>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(submitLink).then(() => showSuccess('Link copied'))}>
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {['PENDING', 'APPROVED', 'REJECTED', 'ALL'].map((key) => (
            <button key={key} type="button" onClick={() => setFilter(key)}
              className={`rounded-full px-3 py-1 text-xs border ${filter === key ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700'}`}>
              {key.charAt(0) + key.slice(1).toLowerCase()} {key !== 'ALL' && `(${counts[key] || 0})`}
            </button>
          ))}
        </div>
        {shown.length === 0 && <p className="text-sm text-gray-500">Nothing here.</p>}
        <ul className="divide-y divide-gray-100">
          {shown.map((t) => (
            <li key={t.id} className="py-3 space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-medium">{t.name}</span>
                  {t.title && <span className="text-sm text-gray-500"> · {t.title}</span>}
                  {t.rating && (
                    <span className="ml-2 inline-flex align-middle">
                      {Array.from({ length: t.rating }, (_, i) => <Star key={i} className="h-3 w-3 text-yellow-400 fill-yellow-400" />)}
                    </span>
                  )}
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_STYLE[t.status]}`}>{t.status.toLowerCase()}</span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-line">{t.message}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span>{formatDateTime(t.createdAt)}</span>
                {t.status !== 'APPROVED' && (
                  <Button size="sm" variant="outline" className="h-7 text-green-700" onClick={() => act(ApiService.reviewTestimonial(t.id, 'APPROVED'), 'Shown on the homepage')}>
                    <Check className="h-3 w-3 mr-1" /> Approve
                  </Button>
                )}
                {t.status !== 'REJECTED' && (
                  <Button size="sm" variant="outline" className="h-7" onClick={() => act(ApiService.reviewTestimonial(t.id, 'REJECTED'), t.status === 'APPROVED' ? 'Hidden from the homepage' : 'Rejected')}>
                    <X className="h-3 w-3 mr-1" /> {t.status === 'APPROVED' ? 'Hide' : 'Reject'}
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => window.confirm('Delete this testimonial?') && act(ApiService.deleteTestimonial(t.id), 'Deleted')}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const NO_ALBUM = '__none';

const AlbumsBar = ({ albums, selected, onSelect, refresh }) => {
  const { showSuccess, showError } = useToast();
  const [eventId, setEventId] = useState('');
  const [name, setName] = useState('');
  const { data: eventsResponse } = useQuery({ queryKey: ['events'], queryFn: () => ApiService.getEvents(0, 100) });
  const events = Array.isArray(eventsResponse?.content) ? eventsResponse.content : [];
  const album = albums.find((a) => a.id === selected);

  const create = async () => {
    try {
      const created = await ApiService.createAlbum({ eventId: eventId || null, name: name.trim() || null });
      setEventId('');
      setName('');
      refresh();
      onSelect(created.id);
      showSuccess('Album created. Photos you upload now go into it.');
    } catch (e) {
      showError(e.message);
    }
  };
  const update = (patch) => ApiService.updateAlbum(album.id, patch).then(refresh).catch((e) => showError(e.message));
  const remove = () => {
    if (!window.confirm(`Delete the album "${album.name}"? Its photos stay in the gallery.`)) return;
    ApiService.deleteAlbum(album.id).then(() => { onSelect(''); refresh(); }).catch((e) => showError(e.message));
  };

  return (
    <div className="rounded-lg border bg-gray-50 p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <FolderOpen className="h-4 w-4 text-gray-500" />
        <select className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm max-w-full" value={selected}
          onChange={(e) => onSelect(e.target.value)} aria-label="Album">
          <option value="">All photos &amp; videos</option>
          <option value={NO_ALBUM}>Not in an album</option>
          {albums.map((a) => (
            <option key={a.id} value={a.id}>{a.name} ({a.photos + a.videos}){a.published ? '' : ' · hidden'}</option>
          ))}
        </select>
      </div>
      {album && (
        <div className="flex flex-wrap items-center gap-2">
          <Input key={album.id} className="h-8 text-sm w-auto flex-1 min-w-[12rem]" defaultValue={album.name} maxLength={120}
            onBlur={(e) => e.target.value.trim() && e.target.value !== album.name && update({ name: e.target.value })} />
          <Button size="sm" variant="outline" className="h-8" onClick={() => update({ published: !album.published })}>
            {album.published ? <><Eye className="h-3 w-3 mr-1" /> Shown</> : <><EyeOff className="h-3 w-3 mr-1" /> Hidden</>}
          </Button>
          <a href={`/gallery/${album.id}`} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline">View on site</a>
          <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={remove}><Trash2 className="h-3 w-3 mr-1" /> Delete album</Button>
        </div>
      )}
      <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
        <select className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm" value={eventId}
          onChange={(e) => setEventId(e.target.value)} aria-label="Event for new album">
          <option value="">New album from event… (optional)</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </select>
        <Input className="h-9" placeholder={eventId ? 'Album name (defaults to the event name)' : 'Album name'} value={name}
          maxLength={120} onChange={(e) => setName(e.target.value)} />
        <Button variant="outline" className="h-9" onClick={create} disabled={!eventId && !name.trim()}>
          <FolderPlus className="h-4 w-4 mr-2" /> Create album
        </Button>
      </div>
    </div>
  );
};

const GallerySection = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [caption, setCaption] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(null);
  const [selected, setSelected] = useState('');
  const { data: allItems = [] } = useQuery({ queryKey: ['adminGallery'], queryFn: () => ApiService.getAdminGallery() });
  const { data: albums = [] } = useQuery({ queryKey: ['adminAlbums'], queryFn: () => ApiService.getAdminAlbums() });
  const targetAlbum = albums.some((a) => a.id === selected) ? selected : null;
  const items = allItems.filter((item) => item.type !== 'LOGO'
    && (selected === '' || (selected === NO_ALBUM ? !item.albumId : item.albumId === selected)));
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['adminGallery'] });
    queryClient.invalidateQueries({ queryKey: ['adminAlbums'] });
    queryClient.invalidateQueries({ queryKey: ['websiteContent'] });
  };

  const uploadPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    let failed = 0;
    for (let i = 0; i < files.length; i += 1) {
      setUploading(`${i + 1}/${files.length}`);
      try {
        await ApiService.uploadGalleryPhoto(files[i], caption, { albumId: targetAlbum });
      } catch (e) {
        failed += 1;
        showError(`${files[i].name}: ${e.message}`);
      }
    }
    setUploading(null);
    setCaption('');
    refresh();
    if (files.length - failed > 0) showSuccess(`${files.length - failed} photo(s) added`);
  };

  const addVideo = async () => {
    try {
      await ApiService.addGalleryVideo(videoUrl, caption, targetAlbum);
      setVideoUrl('');
      setCaption('');
      refresh();
      showSuccess('Video added');
    } catch (e) {
      showError(e.message);
    }
  };

  const update = (id, patch) => ApiService.updateGalleryItem(id, patch).then(refresh).catch((e) => showError(e.message));
  const selectedAlbum = albums.find((a) => a.id === targetAlbum);
  const targetName = selectedAlbum?.name;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gallery</CardTitle>
        <p className="text-sm text-gray-600">Group photos by event in albums. Photos are uploaded here; videos are YouTube or Vimeo links.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <AlbumsBar albums={albums} selected={selected} onSelect={setSelected} refresh={refresh} />
        <p className="text-xs text-gray-500">
          {targetName ? <>New photos and videos go into <span className="font-medium text-gray-700">{targetName}</span>. Use "Set as album cover" on a photo to choose its thumbnail{selectedAlbum.coverId ? '' : ' (the first photo is used until you do)'}.</> : 'New photos and videos are not added to an album. Pick an album above to add them there.'}
        </p>
        <div className="grid gap-2 md:grid-cols-[1fr_auto]">
          <Input placeholder="Caption (optional, applies to what you add next)" value={caption} maxLength={200} onChange={(e) => setCaption(e.target.value)} />
          <label className={`inline-flex items-center justify-center h-10 px-3 rounded-md text-sm text-white cursor-pointer ${uploading ? 'bg-gray-400 pointer-events-none' : 'bg-blue-600 hover:bg-blue-700'}`}>
            <ImagePlus className="h-4 w-4 mr-2" /> {uploading ? `Uploading ${uploading}` : 'Upload photos'}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden" onChange={uploadPhotos} disabled={!!uploading} />
          </label>
          <Input placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/…" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} />
          <Button variant="outline" onClick={addVideo} disabled={!videoUrl.trim()}><Video className="h-4 w-4 mr-2" /> Add video</Button>
        </div>
        {items.length === 0 && <p className="text-sm text-gray-500">No photos or videos here yet.</p>}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((item) => (
            <div key={item.id} className={`rounded-lg border overflow-hidden ${item.published ? '' : 'opacity-60'}`}>
              <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center">
                {item.type === 'PHOTO'
                  ? <img src={ApiService.galleryPhotoUrl(item.id)} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  : (
                    <a href={item.videoUrl} target="_blank" rel="noreferrer" className="relative flex h-full w-full flex-col items-center justify-center text-gray-600 text-xs p-2 break-all">
                      {videoThumbnail(item)
                        ? <><img src={videoThumbnail(item)} alt="" className="absolute inset-0 h-full w-full object-cover" /><Play className="relative h-10 w-10 text-white drop-shadow" /></>
                        : <><Play className="h-8 w-8 mb-1" />{item.videoUrl}</>}
                    </a>
                  )}
              </div>
              <div className="p-2 space-y-2">
                <Input className="h-8 text-xs" defaultValue={item.caption || ''} placeholder="Caption"
                  onBlur={(e) => e.target.value !== (item.caption || '') && update(item.id, { caption: e.target.value })} />
                <select className="h-8 w-full rounded-md border border-gray-300 bg-white px-1 text-xs" value={item.albumId || ''}
                  onChange={(e) => update(item.id, { albumId: e.target.value })} aria-label="Move to album">
                  <option value="">No album</option>
                  {albums.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {item.type === 'PHOTO' && selectedAlbum && item.albumId === selectedAlbum.id && (
                  selectedAlbum.coverId === item.id ? (
                    <span className="inline-flex items-center text-xs font-medium text-green-700"><Check className="h-3 w-3 mr-1" /> Album cover</span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 w-full text-xs"
                      onClick={() => ApiService.updateAlbum(selectedAlbum.id, { coverId: item.id }).then(() => { refresh(); showSuccess('Album cover updated'); }).catch((e) => showError(e.message))}>
                      Set as album cover
                    </Button>
                  )
                )}
                <div className="flex items-center justify-between">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => update(item.id, { published: !item.published })}>
                    {item.published ? <><Eye className="h-3 w-3 mr-1" /> Shown</> : <><EyeOff className="h-3 w-3 mr-1" /> Hidden</>}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-red-600"
                    onClick={() => window.confirm('Delete this item?') && ApiService.deleteGalleryItem(item.id).then(refresh).catch((e) => showError(e.message))}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const LogosSection = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [uploading, setUploading] = useState(false);
  const { data: allItems = [] } = useQuery({ queryKey: ['adminGallery'], queryFn: () => ApiService.getAdminGallery() });
  const logos = allItems.filter((item) => item.type === 'LOGO');
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['adminGallery'] });
    queryClient.invalidateQueries({ queryKey: ['websiteContent'] });
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await ApiService.uploadGalleryPhoto(file, name, { logo: true, link });
      setName('');
      setLink('');
      refresh();
      showSuccess('Logo added');
    } catch (e) {
      showError(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Events page logos</CardTitle>
        <p className="text-sm text-gray-600">Partner, sponsor or association logos shown at the top of the public “View all events” page.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
          <Input placeholder="Name (shown on hover)" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
          <Input placeholder="Link (optional), e.g. https://partner.org" value={link} onChange={(e) => setLink(e.target.value)} />
          <label className={`inline-flex items-center justify-center h-10 px-3 rounded-md text-sm text-white cursor-pointer ${uploading ? 'bg-gray-400 pointer-events-none' : 'bg-blue-600 hover:bg-blue-700'}`}>
            <ImagePlus className="h-4 w-4 mr-2" /> {uploading ? 'Uploading…' : 'Upload logo'}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={upload} disabled={uploading} />
          </label>
        </div>
        {logos.length === 0 && <p className="text-sm text-gray-500">No logos yet.</p>}
        <div className="flex flex-wrap gap-3">
          {logos.map((logo) => (
            <div key={logo.id} className={`w-40 rounded-lg border p-2 ${logo.published ? '' : 'opacity-60'}`}>
              <div className="h-16 flex items-center justify-center bg-white">
                <img src={ApiService.galleryPhotoUrl(logo.id)} alt={logo.caption || ''} className="max-h-16 max-w-full object-contain"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
              </div>
              <div className="mt-1 truncate text-xs text-gray-600" title={logo.link || ''}>{logo.caption || logo.link || '—'}</div>
              <div className="flex justify-between">
                <Button size="sm" variant="ghost" className="h-7 text-xs"
                  onClick={() => ApiService.updateGalleryItem(logo.id, { published: !logo.published }).then(refresh).catch((e) => showError(e.message))}>
                  {logo.published ? 'Shown' : 'Hidden'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-red-600"
                  onClick={() => window.confirm('Delete this logo?') && ApiService.deleteGalleryItem(logo.id).then(refresh).catch((e) => showError(e.message))}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const DocumentLimitSection = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { data } = useQuery({ queryKey: ['documentLimit'], queryFn: () => ApiService.getDocumentLimit() });
  const [mb, setMb] = useState('');
  const shown = mb === '' ? (data?.maxDocumentMb ?? '') : mb;
  const save = () => {
    const value = Number(shown);
    ApiService.saveDocumentLimit(value)
      .then((saved) => {
        setMb('');
        queryClient.setQueryData(['documentLimit'], saved);
        showSuccess(`Documents are limited to ${saved.maxDocumentMb} MB`);
      })
      .catch((e) => showError(e.message || 'Could not save the limit'));
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Document size</CardTitle>
        <p className="text-sm text-gray-600">Largest file someone can upload on a registration form, for every event. 1 to 50 MB.</p>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Input className="w-28" inputMode="numeric" value={shown}
          onChange={(e) => setMb(e.target.value.replace(/[^\d]/g, ''))} />
        <span className="text-sm text-gray-600">MB</span>
        <Button onClick={save} disabled={!shown}>Save</Button>
      </CardContent>
    </Card>
  );
};

const WebsiteContentPage = () => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Website</h1>
      <p className="text-sm text-gray-600">
        Content for the public homepage. Events appear in the “Recent events” carousel when “Show on website” is ticked in the event's settings.
      </p>
    </div>
    <DocumentLimitSection />
    <TestimonialsSection />
    <GallerySection />
    <LogosSection />
  </div>
);

export default WebsiteContentPage;
